import { Injectable } from '@angular/core';
import { UserRoles } from '../dictionaries/UserRoles';
import { UsersService } from './users.service';
import { FirebaseUtilitiesService } from './firebase-utilities.service';

/**
 * Service for handling consultant and user management business logic in ClientProfileComponent
 * Extracted to reduce component complexity and improve maintainability
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileConsultantService {
  constructor(
    private usersService: UsersService,
    private firebaseUtilitiesService: FirebaseUtilitiesService
  ) {}

  /**
   * Gets active consultants for a case
   * @param casename - Case name
   * @param ownerID - Owner ID
   * @param files - Current files array
   * @returns Array of active consultants
   */
  async getActiveConsultants(casename: string, ownerID: string, files: any[]): Promise<any[]> {
    return this.firebaseUtilitiesService.getActiveConsultantsAndClientByClientId(
      [UserRoles.consultant, UserRoles.client],
      casename,
      ownerID,
      files
    );
  }

  /**
   * Gets available consultants for a case
   * @param casename - Case name
   * @param activeConsultants - Array of active consultants
   * @param noclient - Whether to exclude clients
   * @returns Array of available consultants
   */
  async getAvailableConsultants(casename: string, activeConsultants: any[], noclient = false): Promise<any[]> {
    return this.firebaseUtilitiesService.getAvailableConsultantsAndClient(
      casename,
      UserRoles.consultant,
      activeConsultants,
      noclient
    );
  }

  /**
   * Updates facilities consultants lists
   * @param casename - Case name
   * @param ownerID - Owner ID
   * @param files - Current files array
   * @returns Object with active and available consultants and admins
   */
  async updateFacilitiesConsultantsLists(
    casename: string,
    ownerID: string | null,
    files: any[]
  ): Promise<{
    activeConsultants: any[];
    availableConsultants: any[];
    activeAdmins: any[];
    availableAdmins: any[];
  }> {
    // Get all active facility consultants
    const allActiveConsultants = await this.firebaseUtilitiesService.getActiveFaciltiesConsultantsAndClientByClientId(
      [UserRoles.consultant, UserRoles.client],
      casename,
      ownerID,
      files
    );

    // Get all available facility consultants
    const allAvailableConsultants = await this.firebaseUtilitiesService.getAvailableAdmins(
      casename,
      UserRoles.consultant,
      allActiveConsultants,
      true,
      ownerID
    );

    // Get all active facility admins
    const allActiveAdmins = await this.firebaseUtilitiesService.getActiveFaciltiesConsultantsAndClientByClientId(
      [UserRoles.admin],
      casename,
      ownerID,
      files
    );

    // Get all available facility admins
    const allAvailableAdmins = await this.firebaseUtilitiesService.getAvailableAdmins(
      casename,
      UserRoles.admin,
      allActiveAdmins,
      true,
      ownerID
    );

    return {
      activeConsultants: allActiveConsultants || [],
      availableConsultants: allAvailableConsultants || [],
      activeAdmins: allActiveAdmins || [],
      availableAdmins: allAvailableAdmins || []
    };
  }

  /**
   * Finds consultant by email
   * @param email - Consultant email
   * @param consultants - Array of consultants to search
   * @returns Consultant object or null
   */
  findConsultantByEmail(email: string, consultants: any[]): any | null {
    if (!email || !consultants || consultants.length === 0) {
      return null;
    }
    return consultants.find(c => c.email?.toLowerCase() === email.toLowerCase()) || null;
  }

  /**
   * Clears non-existent users from shared users list
   * @param userEmails - Array of user emails to check
   * @returns Object with unLinked and notExistent arrays
   */
  async clearNonExistentUsers(userEmails: string[]): Promise<{ unLinked: string[]; notExistent: string[] }> {
    if (!userEmails || userEmails.length === 0) {
      return { unLinked: [], notExistent: [] };
    }

    const unLinked: string[] = [];
    const notExistent: string[] = [];
    
    for (let index = 0; index < userEmails.length; index++) {
      const userEmail = userEmails[index];
      const found = (await this.usersService.getUserByEmail(userEmail)).docs.length;
      if (!found) {
        notExistent.push(userEmail);
      } else {
        unLinked.push(userEmail);
      }
    }

    return { unLinked, notExistent };
  }
}
